import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { ActionButton, ActionMenuButton, ActionRow, type Action } from './Actions';

export interface RegisterRowProps {
  /** Instrument Sans, left. The name of the thing. Two lines at most; never a control. */
  label: ReactNode;
  /** JetBrains Mono, right-aligned, never wrapped. A date, an amount, a reference. */
  value?: ReactNode;
  /** Second line under the label, in ink-3. The section cited, the funder, a countdown. */
  meta?: ReactNode;
  /** A Badge, after the value. */
  stamp?: ReactNode;
  /** The row opens something: the record, or its edit sheet. */
  onClick?: () => void;
  /** The row is a link. An in-app path renders a router Link; an address with a scheme (https:, mailto:) a plain anchor. */
  href?: string;
  /** Same as `href`, for an in-app path. */
  to?: string;
  /**
   * Read by a screen reader before the label when the row is a button or a
   * link: "Edit", "Open". Visually hidden; the row's whole text stays its name.
   */
  verb?: string;
  /** Before the label: a RowCheck tick box (44px hit area), a glyph. */
  lead?: ReactNode;
  /**
   * What can be done to the row. Never on the label's line on a phone:
   * one action shows as a green text button on a line of its own, two or
   * more collapse into a trailing "More actions" sheet. On a desk they
   * sit at the far right. A ReactNode is placed the same way, as given.
   */
  actions?: ReactNode | Action[];
  /** Two or more actions go in the sheet at every width, not only on a phone. */
  collapseActions?: boolean;
  /** Names the actions sheet and its button: "Actions for Narrative report". Defaults from a string label. */
  actionsLabel?: string;
  /** A closed or historical row: dimmed, still readable. */
  muted?: boolean;
  /** `li` when the rows sit directly in a `<ul>`, which is what they should do. */
  as?: 'div' | 'li';
  className?: string;
}

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * The primary list primitive. Full width, a hairline under each row, label
 * left, value right in the record face, stamp after it. Hover is a
 * green-tint fill where there is a pointer to hover with, a tap gets the
 * same fill while the finger is down, and nothing moves.
 *
 * On a phone (below 640px) the label has the first line to itself, up to
 * two lines; the meta sits on the second line at the left with the value
 * and the stamp at the right. Actions never squeeze the label: one is a
 * green text button on a third line, two or more are one 44px "More
 * actions" control at the end of the row. On a desk the row is one line
 * and the actions sit at the far right.
 *
 * The row itself (label, meta, value, stamp) is the button or link when
 * there is `onClick`, `href` or `to`; the lead and the actions sit beside
 * it, never inside it, so no control is ever nested in another.
 */
export function RegisterRow({
  label,
  value,
  meta,
  stamp,
  onClick,
  href,
  to,
  verb,
  lead,
  actions,
  collapseActions,
  actionsLabel,
  muted,
  as = 'div',
  className,
}: RegisterRowProps) {
  const target = to ?? href;
  const interactive = Boolean(onClick || target);
  const hasValue = value !== undefined && value !== null && value !== false;

  // Phone: the label, then one wrapping line of meta (left) and value and
  // stamp (right); when the three do not fit, the stamp wraps under the
  // value rather than crushing the meta. Desk: the second line dissolves
  // (`contents`) into the row's grid, meta under the label and value and
  // stamp in their own columns.
  const hasSecond = Boolean(meta) || hasValue || Boolean(stamp);
  const cells = (
    <span className="grid w-full grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-x-4">
      <span
        className="line-clamp-2 text-sm text-ink [overflow-wrap:break-word] sm:col-start-1 sm:row-start-1"
        title={typeof label === 'string' ? label : undefined}
      >
        {verb ? <span className="sr-only">{verb} </span> : null}
        {label}
      </span>
      {hasSecond ? (
        <span className="mt-0.5 flex flex-wrap items-baseline justify-end gap-x-3 gap-y-1 sm:contents">
          {meta ? (
            <span className="mr-auto min-w-0 text-xs text-ink-3 sm:col-start-1 sm:row-start-2 sm:mr-0">{meta}</span>
          ) : null}
          {hasValue ? (
            <span className="record whitespace-nowrap text-right text-sm text-ink sm:col-start-2 sm:row-span-2 sm:row-start-1">
              {value}
            </span>
          ) : null}
          {stamp ? (
            <span className="self-center sm:col-start-3 sm:row-span-2 sm:row-start-1 sm:justify-self-end">{stamp}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );

  const mainClass = cx(
    'col-start-2 row-start-1 flex min-h-tap min-w-0 items-center py-2.5 text-left',
    interactive && cx('cursor-pointer active:bg-forest-faint', focusRing),
  );

  let main: ReactNode;
  if (target && SCHEME.test(target)) {
    main = (
      <a href={target} className={mainClass}>
        {cells}
      </a>
    );
  } else if (target) {
    main = (
      <Link to={target} className={mainClass}>
        {cells}
      </Link>
    );
  } else if (onClick) {
    main = (
      <button type="button" onClick={onClick} className={mainClass}>
        {cells}
      </button>
    );
  } else {
    main = <div className={mainClass}>{cells}</div>;
  }

  // Actions: a list of data, or a node placed as given.
  const list = Array.isArray(actions) ? (actions as Action[]) : null;
  const sheetLabel = actionsLabel ?? (typeof label === 'string' ? `Actions for ${label}` : 'More actions');
  let actionCell: ReactNode = null;
  let menuCell: ReactNode = null;
  if (list && list.length === 1) {
    actionCell = <ActionButton action={list[0]} className="-ml-3 sm:ml-0" />;
  } else if (list && list.length > 1) {
    menuCell = <ActionMenuButton actions={list} label={sheetLabel} title={typeof label === 'string' ? label : undefined} />;
    if (!collapseActions) actionCell = <ActionRow actions={list} className="justify-end" />;
  } else if (!list && actions) {
    actionCell = actions as ReactNode;
  }

  const Outer = as;
  return (
    <Outer
      className={cx(
        'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center border-b border-line',
        'transition-colors duration-state ease-ct',
        interactive && 'hover:bg-forest-whisper',
        muted && 'opacity-70',
        className,
      )}
    >
      {lead ? <span className="col-start-1 row-start-1 row-span-2 self-start">{lead}</span> : null}
      {main}
      {actionCell ? (
        <span
          className={cx(
            'col-start-2 row-start-2 -mt-1.5 flex min-w-0 pb-1.5 sm:col-start-3 sm:row-start-1 sm:mt-0 sm:pb-0 sm:pl-2',
            // Two or more inline actions are desk-only; the phone has the sheet.
            list && list.length > 1 && 'hidden sm:flex',
          )}
        >
          {actionCell}
        </span>
      ) : null}
      {menuCell ? (
        <span className={cx('col-start-3 row-start-1 row-span-2 self-center', !collapseActions && 'sm:hidden')}>{menuCell}</span>
      ) : null}
    </Outer>
  );
}

export interface RowCheckProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** The accessible name: "Mark Narrative report done". There is no visible label; the row is the label. */
  label: string;
}

/**
 * The register row's leading tick box: an 18px box in the green, centred in
 * a 44px square that is all hit area, so ticking a row on a phone does not
 * need a fingertip the size of the box. It is pulled 13px left so the box
 * itself lines up with the row's edge and the label starts 31px in, where a
 * checkbox label always has.
 */
export const RowCheck = forwardRef<HTMLInputElement, RowCheckProps>(function RowCheck(
  { label, className, disabled, ...rest },
  ref,
) {
  return (
    <label
      className={cx(
        '-ml-[13px] grid h-tap w-tap cursor-pointer place-items-center',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        aria-label={label}
        disabled={disabled}
        className={cx('h-[18px] w-[18px] cursor-[inherit] accent-[rgb(var(--ct-green-rgb))]', focusRing)}
        {...rest}
      />
    </label>
  );
});

/**
 * The room a RowCheck takes, for a row that has no tick in a list whose
 * other rows do (a closed item, one the reader may not close), so every
 * label in the list starts at the same place.
 */
export function RowLeadSpace() {
  return <span aria-hidden="true" className="block h-tap w-[31px]" />;
}
